import React, { useContext, useEffect, useState } from "react";
import SelectBarnSection from "./SelectBarnSection";
import { Barn, Event, EventEntry } from "../../../models";
import EntryInfoSection from "./EntryInfoSection";
import ClassInfoSection, { OptionGroupsSortedByDiscipline } from "../../EventClass/ClassInfoSection";
import { blankEntry, CreateEntryInterface } from "../../../utilities/eventEntry/CreateEventEntryUtils";
import { PersonContext } from "../../../context/PersonContext";

interface EntryCardProps {
    event: Event;
    currentEventEntry?: EventEntry;
    onSelect: (entryInfo: CreateEntryInterface) => void;
    onValidationChange?: (isValid: boolean) => void;
    onDirtyChange?: (isDirty: boolean) => void;
}

const EntryCard: React.FC<EntryCardProps> = ({ event, currentEventEntry, onSelect, onValidationChange, onDirtyChange }) => {
    const user = useContext(PersonContext);

    const [selectedBarn, setSelectedBarn] = useState<Barn | undefined | null>();
    const [entryInfo, setEntryInfo] = useState<CreateEntryInterface>({
        ...blankEntry,
        userId: user.id,
        eventId: event.id || "",
        organizationId: event.organizationId || ""
    });

    useEffect(() => {
        onSelect(entryInfo);
    }, [entryInfo, onSelect]);

    const handleSelectedBarn = (barn: Barn | null | undefined) => {
        setSelectedBarn(barn);
        setEntryInfo((prevEntryInfo: CreateEntryInterface) => {
            const updatedEntryInfo: CreateEntryInterface = {
                ...prevEntryInfo,
                currentBarn: barn || undefined,
            };
            return updatedEntryInfo;
        });
    }

    const handleEntryInfoUpdate = (newEntryInfo: CreateEntryInterface) => {
        setEntryInfo((prevEntryInfo: CreateEntryInterface) => {
            const updatedEntryInfo: CreateEntryInterface = {
                ...prevEntryInfo,
                ...newEntryInfo,
                userId: user.id,
                eventId: event.id || "",
                organizationId: event.organizationId || "",
                currentBarn: prevEntryInfo.currentBarn,
            };
            return updatedEntryInfo;
        });
    }

    const handleDivisionAndClassData = (data: OptionGroupsSortedByDiscipline[]) => {
        onDirtyChange?.(true);
        setEntryInfo((prevEntryInfo: CreateEntryInterface) => {
            const updatedEntryInfo: CreateEntryInterface = {
                ...prevEntryInfo,
                disciplinesWithSelectedValues: data
            };
            return updatedEntryInfo;
        });
    }

    return (
        <>
            <SelectBarnSection 
                event={event} 
                currentEventEntry={currentEventEntry} 
                onSelect={(barn: Barn | null | undefined) => handleSelectedBarn(barn)} 
                onDirtyChange={onDirtyChange}
            />
            <EntryInfoSection 
                event={event} 
                currentEventEntry={currentEventEntry} 
                selectedBarn={selectedBarn || undefined} 
                onSelect={(entryInfo: CreateEntryInterface) => handleEntryInfoUpdate(entryInfo)}
                onValidationChange={onValidationChange}
                onDirtyChange={onDirtyChange}
            />
            <div data-entry-classes-section>
                <ClassInfoSection
                    event={event}
                    entryInfo={entryInfo}
                    currentEventEntry={currentEventEntry}
                    selectedHorse={entryInfo?.currentHorse}
                    onSelectedDisciplineData={(data: OptionGroupsSortedByDiscipline[]) => handleDivisionAndClassData(data)}
                />
            </div>
        </>
    );
};

export default EntryCard;
